import { CssBaseline } from '@mui/material';
import {
    createTheme,
    ThemeProvider as MUIThemeProvider,
    StyledEngineProvider,
    ThemeOptions,
} from '@mui/material/styles';
import React, { ReactNode, useMemo } from 'react';

import useLocales from 'hooks/useLocales';
import useSettings from 'hooks/useSettings';

import breakpoints from './breakpoints';
import componentsOverride from './overrides';
import palette from './palette';
import shadows, { customShadows } from './shadows';
import shape from './shape';
import typography from './typography';

// ----------------------------------------------------------------------

type Props = {
    children: ReactNode;
};

export default function ThemeProvider({ children }: Props) {
    const { themeMode } = useSettings();
    const { currentLang } = useLocales();

    const isLight = themeMode === 'light';

    const themeOptions: ThemeOptions = useMemo(
        () => ({
            palette: isLight ? palette.light : palette.dark,
            typography,
            breakpoints,
            shape,
            shadows: isLight ? shadows.light : shadows.dark,
            customShadows: isLight ? customShadows.light : customShadows.dark,
        }),
        [isLight],
    );

    const themeWithLocale = React.useMemo(() => createTheme(themeOptions, currentLang), [currentLang, themeOptions]);

    themeWithLocale.components = componentsOverride(themeWithLocale);

    return (
        <StyledEngineProvider injectFirst>
            <MUIThemeProvider theme={themeWithLocale}>
                <CssBaseline />
                {children}
            </MUIThemeProvider>
        </StyledEngineProvider>
    );
}
